import React, { useCallback, useMemo } from 'react';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    type ActiveElement,
    type ChartEvent,
    type ChartOptions,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import type { DonutSegmentData } from './callAnalyticsMix.utils';
import { formatAnalyticsCount } from './callAnalyticsMix.utils';

import styles from './CallStatusDonutChart.module.scss';

ChartJS.register(ArcElement, Tooltip);

/** ~26px ring on a 200px chart */
const CUTOUT_PERCENT = 74;

export interface CallStatusDonutChartProps {
    segments: DonutSegmentData[];
    total: number;
    activeSegmentId?: string | null;
    onSegmentClick?: (segmentId: string) => void;
}

const formatCount = formatAnalyticsCount;

const resolveCssColor = (value: string): string => {
    if (!value.startsWith('var(')) return value;

    const match = value.match(/var\((--[^)]+)\)/);
    if (!match || typeof document === 'undefined') return value;

    return getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim() || value;
};

const toRgba = (color: string, alpha: number): string => {
    const resolved = resolveCssColor(color).trim();

    if (resolved.startsWith('rgb')) {
        const [r, g, b] = resolved.match(/\d+/g) ?? [];
        if (r && g && b) return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    if (resolved.startsWith('#')) {
        const hex = resolved.slice(1);
        const normalized =
            hex.length === 3
                ? hex
                      .split('')
                      .map((char) => char + char)
                      .join('')
                : hex;
        const value = Number.parseInt(normalized, 16);
        const r = (value >> 16) & 255;
        const g = (value >> 8) & 255;
        const b = value & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    return resolved;
};

const getSegmentColor = (segment: DonutSegmentData, activeSegmentId?: string | null): string => {
    const baseColor = resolveCssColor(segment.color);

    if (activeSegmentId && activeSegmentId !== segment.id) {
        return toRgba(baseColor, 0.35);
    }

    return baseColor;
};

export const CallStatusDonutChart: React.FC<CallStatusDonutChartProps> = ({
    segments,
    total,
    activeSegmentId,
    onSegmentClick,
}) => {
    const visibleSegments = useMemo(() => segments.filter((segment) => segment.value > 0), [segments]);

    const chartData = useMemo(
        () => ({
            labels: visibleSegments.map((segment) => segment.label),
            datasets: [
                {
                    data: visibleSegments.map((segment) => segment.value),
                    backgroundColor: visibleSegments.map((segment) => getSegmentColor(segment, activeSegmentId)),
                    borderWidth: 0,
                    spacing: 0,
                    hoverOffset: 6,
                },
            ],
        }),
        [visibleSegments, activeSegmentId]
    );

    const handleClick = useCallback(
        (_event: ChartEvent, elements: ActiveElement[]) => {
            if (!elements.length || !onSegmentClick) return;

            const segment = visibleSegments[elements[0].index];
            if (segment) onSegmentClick(segment.id);
        },
        [onSegmentClick, visibleSegments]
    );

    const options = useMemo<ChartOptions<'doughnut'>>(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            cutout: `${CUTOUT_PERCENT}%`,
            layout: {
                padding: 8,
            },
            animation: {
                animateRotate: true,
                animateScale: false,
            },
            onClick: handleClick,
            onHover: (event, elements) => {
                const target = event.native?.target as HTMLElement | undefined;
                if (target) {
                    target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
                }
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            const value = context.parsed ?? 0;
                            const percent = total > 0 ? Math.round((value / total) * 1000) / 10 : 0;
                            return `${formatCount(value)} (${percent}%)`;
                        },
                    },
                },
            },
        }),
        [handleClick, total]
    );

    return (
        <div
            className={styles.chart}
            data-testid='call-status-donut-chart'
            role='img'
            aria-label='Call status mix chart'
        >
            <div className={styles.canvasWrap}>
                <Doughnut data={chartData} options={options} />
            </div>
            <div className={styles.centerLabel}>
                <span className={styles.totalValue}>{formatCount(total)}</span>
                <span className={styles.totalCaption}>total calls</span>
            </div>
        </div>
    );
};
